import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { REST_API } from '../config';
import { performLogout } from './auth';
import { normalizeUrlPath } from './sentryGrouping';

// ----------------------------------------------------------------------

/**
 * Extra per-request options recognized by the shared axios instance.
 * Set `skipAuthLogout: true` on a request config to opt out of the global
 * 401 auto-logout for that call, while still keeping the request-interceptor
 * behavior (e.g. Authorization header injection) intact.
 */
export interface ReachLinkAxiosConfig extends AxiosRequestConfig {
  skipAuthLogout?: boolean;
}

const axiosInstance = axios.create({
  baseURL: REST_API,
});

// Request interceptor - Add Authorization header
axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  }
);

// Response interceptor - Error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const errorData = error.response?.data;

    const axiosContext: Record<string, unknown> = {};
    if (error.config) {
      if (error.config.url) axiosContext.url = error.config.url;
      if (error.config.method) axiosContext.method = error.config.method;
    }
    if (status !== undefined) axiosContext.status = status;
    if (errorData !== undefined) {
      try {
        axiosContext.data = JSON.stringify(errorData);
      } catch {
        axiosContext.data = 'Unable to stringify error data';
      }
    }

    const method = (error.config?.method ?? 'unknown').toUpperCase();
    const urlPath = error.config?.url ? normalizeUrlPath(error.config.url) : 'unknown';

    Sentry.captureException(error, {
      tags: { 'http.method': method, 'http.path': urlPath },
      fingerprint: ['axios', method, urlPath, status !== undefined ? String(status) : error.code ?? 'network'],
      contexts: {
        axios: axiosContext,
      },
    });

    const skipAuthLogout = (error.config as ReachLinkAxiosConfig | undefined)?.skipAuthLogout;
    if (status === 401 && !skipAuthLogout) {
      performLogout();
    }

    return Promise.reject(errorData || 'Something went wrong');
  }
);

export default axiosInstance;
